/**
 * ForgotPasswordPage — request a password reset link.
 *
 * Reuses the LoginPage's .cs-login-* CSS shell (ambient glow, glass card, tabs)
 * for visual continuity without pulling in the heavy particle canvas.  Always
 * shows a generic success state regardless of whether the email exists, which
 * matches the backend's anti-enumeration response.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ConnectionSourceLogo from "../components/ConnectionSourceLogo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading || !email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
      toast.success("Check your inbox");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send reset link — try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cs-login-root min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div aria-hidden className="cs-login-aurora" />
      <div aria-hidden className="cs-login-orb" />
      <div aria-hidden className="cs-login-grid" />
      <div aria-hidden className="cs-login-scanner" />

      <div className="cs-login-card relative z-10 w-full max-w-md rounded-3xl overflow-hidden">
        <div
          aria-hidden
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{ background: "linear-gradient(90deg, transparent, #CD040B, #F59E0B, #CD040B, transparent)" }}
        />
        <div className="cs-login-inner relative p-7">
          <div className="flex justify-center mb-5">
            <ConnectionSourceLogo size="md" />
          </div>

          {submitted ? (
            <SuccessState email={email} onBack={() => navigate("/login")} />
          ) : (
            <>
              <div className="text-center mb-5">
                <h1
                  className="text-2xl font-black leading-tight mb-1 tracking-tight"
                  style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}
                >
                  Forgot your password?
                </h1>
                <p className="text-xs" style={{ color: "var(--cs-text-muted)" }}>
                  Enter the email you signed up with and we'll send you a secure reset link.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-3" data-testid="forgot-password-form">
                <label className="cs-floating-field block relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    data-testid="forgot-email-input"
                    className="cs-floating-input peer w-full px-4 pt-5 pb-2 rounded-xl outline-none text-sm"
                    style={{
                      background: "var(--cs-bg-hover)",
                      border: "1px solid var(--cs-border)",
                      color: "var(--cs-text)",
                      "--ring-color": "#CD040B",
                    }}
                  />
                  <span
                    className={`cs-floating-label absolute left-4 pointer-events-none transition-all ${email ? "is-up" : ""}`}
                  >
                    Work email
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black flex items-center justify-center gap-2 overflow-hidden relative"
                  data-testid="forgot-submit-btn"
                >
                  <span className="relative z-10" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>
                    {loading ? "Sending…" : "Send reset link"}
                  </span>
                  {loading && <span className="cs-spinner" aria-hidden />}
                </button>

                <div className="pt-1 text-center">
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="cs-forgot-link text-[11px] font-semibold tracking-wide"
                    data-testid="back-to-login-btn"
                  >
                    ← Back to sign in
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessState({ email, onBack }) {
  return (
    <div className="text-center py-2" data-testid="forgot-success-state">
      <div
        className="mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(6,182,212,0.18))",
          border: "1px solid rgba(16,185,129,0.4)",
          boxShadow: "0 0 32px rgba(16,185,129,0.3)",
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>
      <h1
        className="text-2xl font-black leading-tight mb-2 tracking-tight"
        style={{ fontFamily: "Outfit, sans-serif", color: "var(--cs-text)" }}
      >
        Check your inbox
      </h1>
      <p className="text-xs leading-relaxed mb-5" style={{ color: "var(--cs-text-muted)" }}>
        If <strong style={{ color: "var(--cs-text)" }}>{email}</strong> is on file, we've sent a reset link.
        It expires in 60 minutes.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="cs-submit-btn w-full py-3 rounded-xl text-sm font-black"
        data-testid="forgot-success-back-btn"
      >
        <span style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>Back to sign in</span>
      </button>
      <p className="text-[10px] mt-4" style={{ color: "var(--cs-text-muted)" }}>
        Didn't get it? Check spam, or wait 5 minutes and try again.
      </p>
    </div>
  );
}
